<template>
  <div class="sig">
    <!-- Kopf -->
    <header class="sig__head">
      <div class="sig__head-icon"><PenLine :size="18" /></div>
      <div class="sig__head-text">
        <h3 class="sig__title">{{ $gt('E-Mail-Signatur') }}</h3>
        <p class="sig__sub">
          {{ $gt('Die E-Mail-Signatur wird automatisch an jede E-Mail angehängt, die aus KARS für dieses Unternehmen verschickt wird. Die Platzhalter befüllt das System automatisch aus den Benutzerdaten der jeweils angemeldeten Person. Dabei wird auch der Standort mit seinen jeweiligen Kontaktdaten entsprechend berücksichtigt.') }}
        </p>
      </div>
    </header>

    <div class="sig__grid">
      <!-- ── Editor ── -->
      <div class="sig__col">
        <div class="sig__col-label">
          <PenLine :size="12" />
          {{ $gt('Editor') }}
        </div>

        <div class="sig-editor">
          <!-- Grußformel: als eigenes Feld änderbar (Kundenfeedback 11.09.26, I.:
               "Mit freundlichen Grüßen" → z. B. "Freundliche Grüße"). Die
               Auswahl ersetzt die erste Zeile der Signatur; eigener Text
               ist über die Eingabe möglich. -->
          <div class="sig-editor__closing">
            <label class="sig-editor__closing-label" for="sig-closing">
              <MessageSquareQuote :size="13" />
              {{ $gt('Grußformel') }}
            </label>
            <input
              id="sig-closing"
              class="sig-editor__closing-input"
              list="sig-closing-options"
              :placeholder="$gt('z. B. Mit freundlichen Grüßen')"
              type="text"
              :value="closingValue"
              @change="onClosingChange($event.target.value)"
            >
            <datalist id="sig-closing-options">
              <option v-for="c in closingOptions" :key="c" :value="c" />
            </datalist>
            <span class="sig-editor__closing-hint">{{ $gt('Erste Zeile der Signatur – frei änderbar.') }}</span>
          </div>

          <div :aria-label="$gt('Textformatierung')" class="sig-editor__toolbar" role="toolbar">
            <button :title="$gt('Fett')" type="button" @click="execFormat('bold')"><Bold :size="15" /></button>
            <button :title="$gt('Kursiv')" type="button" @click="execFormat('italic')"><Italic :size="15" /></button>
            <button :title="$gt('Unterstrichen')" type="button" @click="execFormat('underline')"><Underline :size="15" /></button>
            <span class="sig-editor__sep" />
            <label class="sig-editor__color" :title="$gt('Textfarbe')">
              <Palette :size="15" />
              <input type="color" @input="execFormat('foreColor', $event.target.value)">
            </label>
          </div>

          <div
            ref="editorRef"
            class="sig-editor__content"
            contenteditable="true"
            data-placeholder="Signatur eingeben…"
            @input="onEditorInput"
            @paste="onEditorPaste"
          />

          <div class="sig-editor__website">
            <label class="sig-editor__website-label" for="sig-website">
              <Globe :size="13" />
              {{ $gt('Webseite des Unternehmens') }}
            </label>
            <input
              id="sig-website"
              v-model.trim="websiteValue"
              class="sig-editor__website-input"
              :placeholder="$gt('https://www.ihre-kanzlei.de')"
              type="url"
            >
            <span class="sig-editor__website-hint">{{ $gt('Befüllt den Platzhalter „Webseite“ in der Signatur. Ohne Eintrag gilt die Homepage der Organisation.') }}</span>
          </div>

          <div class="sig-editor__tokens">
            <div class="sig-editor__tokens-head">
              <span class="sig-editor__tokens-label">{{ $gt('Platzhalter einfügen') }}</span>
              <span class="sig-editor__tokens-actions">
                <button class="sig-editor__template" type="button" @click="showGallery = true">
                  <Sparkles :size="13" />
                  {{ $gt('Layout wählen') }}
                </button>
                <button class="sig-editor__template" type="button" @click="showImport = true">
                  <ClipboardPaste :size="13" />
                  {{ $gt('Aus Text/HTML einfügen') }}
                </button>
              </span>
            </div>
            <div class="sig-editor__chips">
              <button
                v-for="p in placeholders"
                :key="p.key"
                class="sig-chip"
                :class="{ 'is-used': signatureHtml.includes(p.key) }"
                :title="chipTitle(p)"
                type="button"
                @click="togglePlaceholder(p.key)"
              >
                <component :is="signatureHtml.includes(p.key) ? Check : Plus" :size="12" />
                {{ p.label }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Live-Vorschau als E-Mail-Fenster ── -->
      <div class="sig__col">
        <div class="sig__col-label sig__col-label--live">
          <span aria-hidden="true" class="sig__live-dot" />
          {{ $gt('Live-Vorschau') }}
        </div>

        <div class="sig-stage">
          <div class="sig-mail">
            <div class="sig-mail__titlebar">
              <span class="sig-mail__dot sig-mail__dot--red" />
              <span class="sig-mail__dot sig-mail__dot--yellow" />
              <span class="sig-mail__dot sig-mail__dot--green" />
              <span class="sig-mail__titlebar-text">{{ $gt('Neue Nachricht') }}</span>
              <Mail class="sig-mail__titlebar-icon" :size="13" />
            </div>

            <div class="sig-mail__fields">
              <div class="sig-mail__field">
                <span class="sig-mail__field-label">{{ $gt('An') }}</span>
                <span class="sig-mail__recipient">
                  <span class="sig-mail__avatar">E</span>
                  {{ $gt('Empfänger') }}
                </span>
              </div>
              <div class="sig-mail__field">
                <span class="sig-mail__field-label">{{ $gt('Betreff') }}</span>
                <span class="sig-mail__subject">{{ $gt('Ihre Anfrage') }}</span>
              </div>
            </div>

            <div class="sig-mail__body">
              <p class="sig-mail__lead">{{ $gt('Sehr geehrte Damen und Herren,') }}</p>
              <p class="sig-mail__lead">…</p>

              <div v-if="signatureHtml" class="sig-mail__signature">
                <span class="sig-mail__signature-tag">{{ $gt('Ihre Signatur') }}</span>
                <div class="sig-mail__signature-html" v-html="renderedSignature" />
              </div>
              <div v-else class="sig-mail__empty">
                <PenLine :size="15" />
                {{ $gt('Signatur erscheint hier, sobald Sie etwas eingeben.') }}
              </div>

              <div class="sig-mail__legal">
                <p class="sig-mail__company">
                  {{ companyLine }}<br>
                  <template v-if="mainLocation?.address">{{ mainLocation.address }}</template>
                </p>
                <p class="sig-mail__confidential">
                  {{ $gt('Diese E-Mail enthält vertrauliche und/oder rechtlich geschützte Informationen. Wenn Sie nicht der richtige Adressat sind oder diese Mail irrtümlich erhalten haben, informieren Sie bitte sofort den Absender und vernichten Sie diese Mail. Das unerlaubte Kopieren sowie unbefugte Weitergabe dieser Mail ist nicht gestattet.') }}
                </p>
                <p class="sig-mail__confidential">
                  {{ $gt('The information contained in this e-mail is confidential. It is intended solely for the addressee. Access to this e-mail by anyone else is unauthorized. If you are not the intended recipient, any form of disclosure, reproduction, distribution or any action taken or refrains from reliance on it, is prohibited and may be unlawful. Please notify the sender immediately and delete this e-mail.') }}
                </p>
              </div>
            </div>

            <div aria-hidden="true" class="sig-mail__foot">
              <span class="sig-mail__send"><Send :size="13" /> {{ $gt('Senden') }}</span>
            </div>
          </div>
        </div>

        <p class="sig__hint">
          <Info :size="13" />
          {{ $gt('Pflichtangaben und Vertraulichkeitshinweis (Deutsch/Englisch) ergänzt das System automatisch aus den Stammdaten – Sie müssen sie nicht pflegen.') }}
        </p>
      </div>
    </div>

    <footer v-if="!hideActions" class="sig__actions">
      <span v-if="dirty" class="sig__dirty">
        <span class="sig__dirty-dot" />
        {{ $gt('Ungespeicherte Änderungen') }}
      </span>
      <div class="sig__actions-buttons">
        <button v-if="dirty" class="sig-btn" type="button" @click="reset">{{ $gt('Verwerfen') }}</button>
        <button class="sig-btn sig-btn--primary" :disabled="!dirty || saving" type="button" @click="emit('save', { signatureHtml, website: websiteValue })">
          <Check :size="15" />
          {{ saving ? $gt('Speichert…') : $gt('Signatur speichern') }}
        </button>
      </div>
    </footer>

    <!-- Galerie: 4 Signatur-Layouts mit Live-Vorschau -->
    <v-dialog v-model="showGallery" max-width="980" scrollable>
      <v-card rounded="lg">
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="sig-gallery__title">
            <Sparkles :size="18" />
            {{ $gt('Signatur-Layout wählen') }}
          </span>
          <button :aria-label="$gt('Schließen')" class="sig-gallery__close" type="button" @click="showGallery = false">
            <X :size="18" />
          </button>
        </v-card-title>
        <v-card-text class="pa-4 pt-0">
          <p class="sig-gallery__lead">
            {{ $gt('Wählen Sie ein Layout als Ausgangspunkt – Platzhalter und Unternehmensname sind bereits eingesetzt. Danach können Sie den Text frei anpassen.') }}
          </p>
          <div class="sig-gallery">
            <button
              v-for="layout in layouts"
              :key="layout.key"
              class="sig-gallery__card"
              type="button"
              @click="applyLayout(layout.key)"
            >
              <span class="sig-gallery__card-head">
                <span class="sig-gallery__card-title">{{ $gt(layout.title) }}</span>
                <span class="sig-gallery__card-desc">{{ $gt(layout.description) }}</span>
              </span>
              <span class="sig-gallery__card-preview" v-html="layoutPreview(layout)" />
              <span class="sig-gallery__card-cta">
                <Check :size="13" />
                {{ $gt('Übernehmen') }}
              </span>
            </button>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <ImportTextDialog v-model="showImport" :title="$gt('Signatur aus Text/HTML einfügen')" @import="onImport" />

    <YesNoDialog
      ref="replaceDialog"
      :content="$gt('Die vorhandene Signatur wird durch das gewählte Layout ersetzt. Ungespeicherte Änderungen gehen verloren.')"
      no-button-icon-type="cancel"
      no-button-text="Abbrechen"
      :on-yes="confirmReplace"
      :title="$gt('Signatur ersetzen?')"
      type="warning"
      yes-button-icon-type="check"
      yes-button-text="Ersetzen"
    />
  </div>
</template>

<script setup>
  import {
    Bold,
    Check,
    ClipboardPaste,
    Globe,
    Info,
    Italic,
    Mail,
    MessageSquareQuote,
    Palette,
    PenLine,
    Plus,
    Send,
    Sparkles,
    Underline,
    X,
  } from 'lucide-vue-next'
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import ImportTextDialog from '@/components/messages/templates/ImportTextDialog.vue'
  import YesNoDialog from '@/components/windows/dialogs/YesNoDialog.vue'
  import { useAuthStore } from '@/stores/authStore'
  import { SIGNATURE_LAYOUTS } from '@/utils/signatureLayouts'
  import { normalizePhone, removePlaceholderFromHtml, renderSignatureHtml } from '@/utils/signatureTemplate'
  import { CLOSING_OPTIONS, extractClosing, replaceClosing } from '@/utils/signatureClosing'

  const props = defineProps({
    /** Zuletzt gespeicherte Signatur (HTML) aus den Kommunikations-Einstellungen. */
    savedSignature: { type: String, default: '' },
    /** Aktives Unternehmen ({ name, legalForm, … }) für die Pflichtangaben. */
    company: { type: Object, default: null },
    /** Hauptsitz ({ name, address }) für die Pflichtangaben. */
    mainLocation: { type: Object, default: null },
    /** Webseite des Unternehmens (Fallback: Organisations-Homepage). */
    savedWebsite: { type: String, default: '' },
    saving: { type: Boolean, default: false },
    /**
     * Eigene Aktionsleiste (Speichern/Verwerfen) ausblenden — für Einbettungen
     * wie das Onboarding, wo der umgebende Wizard das Speichern übernimmt und
     * die Werte per getValues() (defineExpose) abholt.
     */
    hideActions: { type: Boolean, default: false },
  })

  const emit = defineEmits(['save'])

  const signatureHtml = ref(props.savedSignature)
  const websiteValue = ref(props.savedWebsite)
  const editorRef = ref(null)

  const dirty = computed(() =>
    signatureHtml.value !== props.savedSignature
    || websiteValue.value !== props.savedWebsite,
  )

  function syncEditor () {
    if (editorRef.value && editorRef.value.innerHTML !== signatureHtml.value) {
      editorRef.value.innerHTML = signatureHtml.value
    }
  }

  onMounted(syncEditor)

  // Nachladen (z. B. Firmenwechsel) übernehmen, solange nicht editiert wird.
  watch(() => props.savedSignature, value => {
    if (dirty.value) return
    signatureHtml.value = value
    syncEditor()
  })

  watch(() => props.savedWebsite, value => {
    if (dirty.value) return
    websiteValue.value = value
  })

  const placeholders = [
    { key: '{{titel}}', label: 'Titel' },
    { key: '{{vorname}}', label: 'Vorname' },
    { key: '{{nachname}}', label: 'Nachname' },
    { key: '{{stellenbezeichnung}}', label: 'Stellenbezeichnung' },
    { key: '{{email}}', label: 'E-Mail' },
    { key: '{{mobil}}', label: 'Mobilnummer' },
    { key: '{{festnetz}}', label: 'Festnetz' },
    { key: '{{benutzername}}', label: 'Benutzername' },
    { key: '{{organisation}}', label: 'Unternehmen' },
    { key: '{{webseite}}', label: 'Webseite' },
    // Standort-Platzhalter: werden beim Versand mit den Daten des im
    // Composer gewählten Standorts befüllt (Mehrfach-Standort-Zuordnung).
    { key: '{{standort}}', label: 'Standort' },
    { key: '{{standort_adresse}}', label: 'Standort-Adresse' },
    { key: '{{standort_telefon}}', label: 'Standort-Telefon' },
    { key: '{{standort_email}}', label: 'Standort-E-Mail' },
  ]

  // Standort (Name inkl. Anschrift) und Standort-Adresse würden dieselbe
  // Anschrift doppelt in die Signatur bringen — nur eines von beiden zulassen.
  const EXCLUSIVE_PLACEHOLDERS = {
    '{{standort}}': '{{standort_adresse}}',
    '{{standort_adresse}}': '{{standort}}',
  }

  function onEditorInput () {
    signatureHtml.value = editorRef.value?.innerHTML || ''
  }

  function onEditorPaste (e) {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }

  function execFormat (command, value) {
    editorRef.value?.focus()
    document.execCommand(command, false, value || null)
    onEditorInput()
  }

  // Letzte Cursorposition im Editor merken: Beim Klick auf einen Chip ist
  // die Auswahl bereits weg (der Button erhält den Fokus), deshalb wird sie
  // laufend über selectionchange gesichert und beim Einfügen wiederhergestellt.
  let savedRange = null

  function rememberSelection () {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return
    const range = sel.getRangeAt(0)
    if (editorRef.value?.contains(range.commonAncestorContainer)) {
      savedRange = range.cloneRange()
    }
  }

  onMounted(() => document.addEventListener('selectionchange', rememberSelection))
  onBeforeUnmount(() => document.removeEventListener('selectionchange', rememberSelection))

  function focusEditorCaret () {
    const el = editorRef.value
    if (!el) return
    el.focus()
    const sel = window.getSelection()
    if (!sel) return
    sel.removeAllRanges()
    if (savedRange && el.contains(savedRange.commonAncestorContainer)) {
      sel.addRange(savedRange)
      return
    }
    // Ohne gemerkte Position ans Ende der Signatur — nie an den Anfang,
    // sonst landet der Platzhalter vor dem Grußtext (Kundenfeedback).
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    sel.addRange(range)
  }

  /**
   * Chip-Klick als Umschalter: Ein noch nicht verwendeter Platzhalter wird
   * an der Cursorposition eingefügt, ein bereits verwendeter wieder aus der
   * Signatur entfernt — Mehrfach-Einfügen durch wiederholtes Klicken ist
   * damit ausgeschlossen (Kundenfeedback 19.08.26).
   */
  function togglePlaceholder (key) {
    if (signatureHtml.value.includes(key)) {
      removePlaceholder(key)
      return
    }
    const conflicting = EXCLUSIVE_PLACEHOLDERS[key]
    if (conflicting && signatureHtml.value.includes(conflicting)) {
      removePlaceholder(conflicting)
    }
    // Kundenreview 23.08.26 (II.2): Mit Cursor im Editor wird an der
    // Cursorposition eingefügt. OHNE Cursorposition (z. B. nach Entfernen
    // und erneutem Anklicken) landet der Platzhalter nicht mehr irgendwo am
    // Ende, sondern an seiner festen, strukturellen Position.
    const el = editorRef.value
    const hasCaret = !!(savedRange && el && el.contains(savedRange.commonAncestorContainer))
    if (hasCaret) {
      focusEditorCaret()
      document.execCommand('insertText', false, key)
      onEditorInput()
      rememberSelection()
    } else {
      insertAtCanonicalPosition(key)
    }
  }

  // Feste Signatur-Struktur: Name-Zeile, darunter Unternehmen, dann
  // Kontaktzeilen, Webseite, Standort-Angaben.
  const CANONICAL_ORDER = [
    '{{titel}}', '{{vorname}}', '{{nachname}}',
    '{{stellenbezeichnung}}',
    '{{organisation}}',
    '{{festnetz}}', '{{mobil}}',
    '{{email}}', '{{webseite}}', '{{benutzername}}',
    '{{standort}}', '{{standort_adresse}}', '{{standort_telefon}}', '{{standort_email}}',
  ]
  const SAME_LINE = new Set(['{{titel}}', '{{vorname}}', '{{nachname}}'])

  /**
   * Fügt einen Platzhalter an seiner kanonischen Position ein: hinter dem
   * letzten bereits vorhandenen Vorgänger (auf neuer Zeile bzw. in der
   * Namenszeile mit Leerzeichen), sonst vor dem ersten Nachfolger, sonst
   * ans Ende. Dadurch ist die Anordnung immer gleich und nachvollziehbar.
   */
  function insertAtCanonicalPosition (key) {
    const html = signatureHtml.value || ''
    const idx = CANONICAL_ORDER.indexOf(key)
    let insertion = null

    if (idx !== -1) {
      for (let i = idx - 1; i >= 0 && !insertion; i--) {
        const anchor = CANONICAL_ORDER[i]
        const p = html.lastIndexOf(anchor)
        if (p === -1) continue
        const after = p + anchor.length
        if (SAME_LINE.has(key) && SAME_LINE.has(anchor)) {
          insertion = { pos: after, text: ' ' + key }
        } else {
          const lineEnd = /<br[^>]*>|<\/p>|<\/div>/gi
          lineEnd.lastIndex = after
          const m = lineEnd.exec(html)
          insertion = m
            ? { pos: m.index, text: '<br>' + key }
            : { pos: html.length, text: '<br>' + key }
        }
      }
      for (let i = idx + 1; i < CANONICAL_ORDER.length && !insertion; i++) {
        const anchor = CANONICAL_ORDER[i]
        const p = html.indexOf(anchor)
        if (p === -1) continue
        insertion = {
          pos: p,
          text: SAME_LINE.has(key) && SAME_LINE.has(anchor) ? key + ' ' : key + '<br>',
        }
      }
    }
    if (!insertion) insertion = { pos: html.length, text: (html ? '<br>' : '') + key }

    const next = html.slice(0, insertion.pos) + insertion.text + html.slice(insertion.pos)
    signatureHtml.value = next
    if (editorRef.value) editorRef.value.innerHTML = next
    savedRange = null
  }

  // Nimmt Beschriftung und Trennzeichen mit ("Tel: … | Mobil:" bleibt
  // sonst als Fragment stehen – Kundenfeedback 11.09.26).
  function removePlaceholder (key) {
    const cleaned = removePlaceholderFromHtml(signatureHtml.value, key)
    signatureHtml.value = cleaned
    if (editorRef.value) editorRef.value.innerHTML = cleaned
    savedRange = null
  }

  function chipTitle (p) {
    if (signatureHtml.value.includes(p.key)) {
      return 'Klick entfernt den Platzhalter aus der Signatur'
    }
    const conflicting = EXCLUSIVE_PLACEHOLDERS[p.key]
    if (conflicting && signatureHtml.value.includes(conflicting)) {
      return 'Ersetzt den Platzhalter ' + conflicting + ' – Standort und Standort-Adresse sind nur alternativ wählbar'
    }
    return 'Einfügen: ' + p.key
  }

  // ── Layout-Galerie & Import ──────────────────────────────────────────
  // Statt einer einzigen "üblichen Vorlage" stehen mehrere Layouts zur
  // Wahl (klassisch, kompakt, mit Standortblock, mit Webseite/Hinweis);
  // bestehende Kanzlei-Signaturen lassen sich aus Text/HTML übernehmen.
  const layouts = SIGNATURE_LAYOUTS
  const showGallery = ref(false)
  const showImport = ref(false)
  const replaceDialog = ref(null)
  let pendingLayoutKey = null

  function setSignature (html) {
    signatureHtml.value = html
    if (editorRef.value) editorRef.value.innerHTML = html
    savedRange = null
  }

  function buildLayout (key) {
    const layout = layouts.find(l => l.key === key) || layouts[0]
    return layout.build(props.company?.name || '')
  }

  function hasVisibleSignature () {
    return !!signatureHtml.value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim()
  }

  function applyLayout (key) {
    showGallery.value = false
    if (hasVisibleSignature()) {
      pendingLayoutKey = key
      replaceDialog.value?.openDialog()
      return
    }
    setSignature(buildLayout(key))
  }

  function confirmReplace () {
    if (pendingLayoutKey) setSignature(buildLayout(pendingLayoutKey))
    pendingLayoutKey = null
  }

  function onImport (html) {
    setSignature(html)
  }

  /** Übliche Vorlage (klassisches Layout) – weiterhin für das Onboarding. */
  function insertDefaultTemplate () {
    setSignature(buildLayout('classic'))
  }

  // Für Einbettungen mit hideActions (z. B. Onboarding): Der umgebende
  // Wizard holt die aktuellen Werte ab und kann die übliche Vorlage
  // programmatisch vorbefüllen.
  defineExpose({
    getValues: () => ({ signatureHtml: signatureHtml.value, website: websiteValue.value }),
    insertDefaultTemplate,
  })

  const authStore = useAuthStore()

  const previewValues = computed(() => ({
    vorname: authStore.firstName || 'Max',
    nachname: authStore.lastName || 'Mustermann',
    email: authStore.email || 'max@beispiel.de',
    // Telefonnummern ohne Beispiel-Fallback: Fehlt die Nummer in den Daten,
    // verschwindet die Angabe samt Beschriftung auch in der Vorschau —
    // exakt wie später in der verschickten E-Mail (Kundenfeedback 19.08.26).
    mobil: normalizePhone(authStore.signatureMobile),
    festnetz: normalizePhone(authStore.landline),
    titel: authStore.title,
    // Ohne Beispiel-Fallback: Die Vorschau zeigte sonst eine fremde
    // Stellenbezeichnung ("Rechtsanwaltsfachangestellte"), die in der
    // verschickten E-Mail gar nicht steht (Kundenfeedback 11.09.26).
    // Fehlt die Stellenbezeichnung, steht ersatzweise die Abteilung in der
    // Signatur (z. B. "Serviceabteilung").
    stellenbezeichnung: authStore.signatureJobTitle,
    benutzername: authStore.userName,
    organisation: props.company?.name || authStore.organizationName,
    webseite: websiteValue.value,
    homepage: websiteValue.value,
    // Standort-Vorschau: Hauptsitz als Beispiel; beim Versand gilt der im
    // Composer gewählte Standort des Mitarbeiters.
    standort: props.mainLocation?.name || 'Hauptsitz',
    standort_adresse: props.mainLocation?.address || 'Musterstraße 1, 12345 Musterstadt',
    standort_telefon: normalizePhone(props.mainLocation?.landline),
    standort_email: props.mainLocation?.email || 'standort@beispiel.de',
  }))

  // Gemeinsames Rendering (utils/signatureTemplate): ersetzt nur in
  // Textknoten, hebt befüllte Werte hervor und entfernt Fragmente
  // leerer Angaben ("Tel:  |").
  const renderedSignature = computed(() =>
    renderSignatureHtml(
      signatureHtml.value,
      previewValues.value,
      value => `<span class="sig-var">${value}</span>`,
    ),
  )

  // Mini-Vorschau eines Galerie-Layouts mit denselben Beispieldaten.
  function layoutPreview (layout) {
    return renderSignatureHtml(
      layout.build(props.company?.name || 'Ihr Unternehmen'),
      previewValues.value,
      value => `<span class="sig-var">${value}</span>`,
    )
  }

  // Pflichtangaben-Zeile aus den Stammdaten (Name + Rechtsform).
  const companyLine = computed(() => {
    const name = props.company?.name || ''
    const legal = props.company?.legalForm || ''
    return legal && !name.includes(legal) ? `${name} · ${legal}` : name
  })

  function reset () {
    signatureHtml.value = props.savedSignature
    websiteValue.value = props.savedWebsite
    syncEditor()
  }

  // ── Grußformel ───────────────────────────────────────────────────────
  const closingOptions = CLOSING_OPTIONS
  const closingValue = computed(() => extractClosing(signatureHtml.value))

  function onClosingChange (value) {
    const next = replaceClosing(signatureHtml.value, value)
    if (next === signatureHtml.value) return
    setSignature(next)
  }
</script>

<script>
  export default { name: 'CompanySignatureEditor' }
</script>

<style scoped lang="scss">
/* ── Grußformel ── */
.sig-editor__closing {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 6px 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}
.sig-editor__closing-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
}
.sig-editor__closing-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.85rem;
  background: #fff;
  &:focus { outline: none; border-color: #0076e5; box-shadow: 0 0 0 3px rgba(0, 118, 229, 0.15); }
}
.sig-editor__closing-hint {
  grid-column: 2;
  font-size: 0.7rem;
  color: #64748b;
}

/* ── Galerie ── */
.sig-editor__tokens-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.sig-gallery__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;

  svg {
    color: var(--primary-color, #0076e5);
  }
}

.sig-gallery__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--grey-color, #5f5f5f);
  cursor: pointer;

  &:hover {
    background: var(--grey-light-color, #f3f5f7);
    color: var(--dark-color, #323232);
  }
}

.sig-gallery__lead {
  margin: 0 0 14px;
  font-size: 0.82rem;
  color: var(--grey-color, #5f5f5f);
}

.sig-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
}

.sig-gallery__card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 14px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: var(--primary-color, #0076e5);
    box-shadow: 0 6px 18px -8px rgba(0, 118, 229, 0.45);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color, #0076e5);
    outline-offset: 2px;
  }
}

.sig-gallery__card-head {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sig-gallery__card-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--dark-color, #323232);
}

.sig-gallery__card-desc {
  font-size: 0.74rem;
  line-height: 1.4;
  color: var(--grey-color, #5f5f5f);
}

.sig-gallery__card-preview {
  display: block;
  flex: 1;
  padding: 12px;
  border: 1px dashed var(--border-grey-color-light, #e6eaf0);
  border-radius: 10px;
  background: #fbfcfe;
  font-size: 0.76rem;
  line-height: 1.5;
  color: var(--dark-color, #323232);
  overflow: hidden;

  :deep(.sig-var) {
    padding: 0 2px;
    border-radius: 3px;
    background: rgba(0, 118, 229, 0.1);
    color: var(--primary-color, #0076e5);
  }
}

.sig-gallery__card-cta {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-end;
  font-size: 0.76rem;
  font-weight: 650;
  color: var(--primary-color, #0076e5);
}

/* ── Kopf ── */
.sig__head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 18px;
}

.sig__head-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--primary-color, #0076e5), #38a1f7);
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 118, 229, 0.28);
}

.sig__title {
  margin: 0 0 3px;
  font-size: 1.05rem;
  font-weight: 750;
  color: var(--dark-color, #323232);
}

.sig__sub {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.5;
  color: var(--grey-color, #5f5f5f);
  max-width: 760px;
}

/* ── Layout ── */
.sig__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 22px;
}

@media (max-width: 1080px) {
  .sig__grid {
    grid-template-columns: 1fr;
  }
}

.sig__col-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--grey-color, #5f5f5f);

  svg {
    color: var(--primary-color, #0076e5);
  }
}

.sig__live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
  animation: sig-pulse 2s ease-in-out infinite;
}

@keyframes sig-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
  }

  50% {
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.06);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sig__live-dot {
    animation: none;
  }
}

/* ── Editor-Karte ── */
.sig-editor {
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: var(--primary-color, #0076e5);
    box-shadow: 0 0 0 3px rgba(0, 118, 229, 0.12);
  }
}

.sig-editor__toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 8px 10px;
  background: var(--grey-light-color, #f3f5f7);
  border-bottom: 1px solid var(--border-grey-color-light, #e6eaf0);

  button,
  .sig-editor__color {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: none;
    background: transparent;
    border-radius: 7px;
    cursor: pointer;
    color: var(--dark-color, #323232);
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover {
      background: rgba(0, 118, 229, 0.1);
      color: var(--primary-color, #0076e5);
    }
  }
}

.sig-editor__sep {
  width: 1px;
  height: 18px;
  background: var(--border-grey-color, #d0d5dd);
  margin: 0 6px;
}

.sig-editor__color {
  position: relative;
  overflow: hidden;

  input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
  }
}

.sig-editor__content {
  min-height: 190px;
  max-height: 360px;
  overflow-y: auto;
  padding: 16px 18px;
  font-size: 0.88rem;
  line-height: 1.6;
  outline: none;

  &:empty::before {
    content: attr(data-placeholder);
    color: #a6aeb8;
    pointer-events: none;
  }
}

/* Webseiten-Feld für den {{webseite}}-Platzhalter */
.sig-editor__website {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: 10px;
  row-gap: 3px;
  padding: 10px 12px;
  border-top: 1px dashed var(--border-grey-color, #d0d5dd);
  background: #fbfcfe;
}

.sig-editor__website-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 650;
  color: var(--dark-color, #323232);
  white-space: nowrap;

  svg {
    color: var(--primary-color, #0076e5);
  }
}

.sig-editor__website-input {
  padding: 6px 10px;
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 8px;
  font-size: 0.82rem;
  color: var(--dark-color, #323232);
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: var(--primary-color, #0076e5);
    box-shadow: 0 0 0 3px rgba(0, 118, 229, 0.12);
  }
}

.sig-editor__website-hint {
  grid-column: 2;
  font-size: 0.7rem;
  color: var(--grey-color, #5f5f5f);
}

/* Platzhalter-Leiste direkt am Editor */
.sig-editor__tokens {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px dashed var(--border-grey-color, #d0d5dd);
  background: #fbfcfe;
}

.sig-editor__tokens-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.sig-editor__tokens-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--grey-color, #5f5f5f);
}

.sig-editor__template {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: none;
  border-radius: 999px;
  background: var(--primary-color, #0076e5);
  color: #fff;
  font-size: 0.76rem;
  font-weight: 650;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: #0069cc;
  }
}

.sig-editor__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.sig-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 11px;
  background: #fff;
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 999px;
  font-size: 0.78rem;
  color: var(--dark-color, #323232);
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: var(--primary-color, #0076e5);
    color: var(--primary-color, #0076e5);
  }

  /* Bereits verwendete Platzhalter sind markiert (Inhaber-Feedback). */
  &.is-used {
    border-color: var(--primary-color, #0076e5);
    background: var(--primary-light-color, #deeefb);
    color: var(--primary-color, #0076e5);
    font-weight: 650;
  }
}

/* ── Vorschau: E-Mail-Fenster ── */
.sig-stage {
  padding: 18px;
  border-radius: 16px;
  border: 1px solid #e3ebf4;
  background: radial-gradient(120% 90% at 15% 0%, #e8f3fe 0%, #f2f7fc 55%, #eef2f7 100%);
}

.sig-mail {
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 12px 28px -10px rgba(16, 24, 40, 0.18),
    0 2px 6px rgba(16, 24, 40, 0.06);
}

.sig-mail__titlebar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 12px;
  background: linear-gradient(#f7f9fc, #f1f4f9);
  border-bottom: 1px solid #e6eaf0;
}

.sig-mail__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;

  &--red {
    background: #ff5f57;
  }

  &--yellow {
    background: #febc2e;
  }

  &--green {
    background: #28c840;
  }
}

.sig-mail__titlebar-text {
  flex: 1;
  text-align: center;
  font-size: 0.74rem;
  font-weight: 600;
  color: #8a94a3;
}

.sig-mail__titlebar-icon {
  color: #b3bcc9;
  flex-shrink: 0;
}

.sig-mail__fields {
  padding: 2px 14px;
  border-bottom: 1px solid #eef1f6;
}

.sig-mail__field {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  font-size: 0.8rem;

  & + & {
    border-top: 1px solid #f1f4f8;
  }
}

.sig-mail__field-label {
  width: 52px;
  color: #8a94a3;
  flex-shrink: 0;
}

.sig-mail__recipient {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px 2px 3px;
  background: var(--primary-light-color, #deeefb);
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--primary-color, #0076e5);
}

.sig-mail__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--primary-color, #0076e5);
  color: #fff;
  font-size: 0.64rem;
  font-weight: 700;
}

.sig-mail__subject {
  color: var(--dark-color, #323232);
  font-weight: 600;
}

.sig-mail__body {
  padding: 16px 18px;
  font-size: 0.88rem;
  line-height: 1.6;
  color: var(--dark-color, #323232);
}

.sig-mail__lead {
  margin: 0 0 4px;
  color: var(--grey-color, #5f5f5f);
}

/* Markierter Signatur-Bereich, damit sofort erkennbar ist, was die
   Signatur in der fertigen E-Mail ausmacht. */
.sig-mail__signature {
  position: relative;
  margin-top: 18px;
  padding: 16px 14px 12px;
  border: 1.5px dashed rgba(0, 118, 229, 0.4);
  border-radius: 10px;
  background: rgba(0, 118, 229, 0.03);
}

.sig-mail__signature-tag {
  position: absolute;
  top: -9px;
  left: 12px;
  padding: 1px 9px;
  background: var(--primary-color, #0076e5);
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border-radius: 999px;
}

.sig-mail__signature-html :deep(.sig-var) {
  background: rgba(0, 118, 229, 0.08);
  border-radius: 4px;
  padding: 0 3px;
}

.sig-mail__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 18px;
  padding: 18px 14px;
  border: 1.5px dashed var(--border-grey-color, #d0d5dd);
  border-radius: 10px;
  color: var(--grey-color, #5f5f5f);
  font-size: 0.8rem;
  font-style: italic;

  svg {
    flex-shrink: 0;
    color: #a6aeb8;
  }
}

.sig-mail__legal {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eef1f6;
}

.sig-mail__company {
  margin: 0 0 8px;
  font-size: 0.76rem;
  color: var(--grey-color, #5f5f5f);
}

.sig-mail__confidential {
  margin: 0 0 4px;
  font-size: 0.68rem;
  line-height: 1.45;
  color: #9aa5b3;
}

.sig-mail__foot {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-top: 1px solid #eef1f6;
  background: #fafbfd;
}

.sig-mail__send {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  background: var(--primary-color, #0076e5);
  color: #fff;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 650;
  opacity: 0.9;
}

.sig__hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 10px 0 0;
  font-size: 0.76rem;
  line-height: 1.45;
  color: var(--grey-color, #5f5f5f);

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--primary-color, #0076e5);
  }
}

/* ── Aktionen ── */
.sig__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid var(--border-grey-color-light, #eef1f6);
}

.sig__dirty {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  font-size: 0.76rem;
  font-weight: 600;
}

.sig__dirty-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f59e0b;
}

.sig__actions-buttons {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.sig-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 15px;
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 10px;
  background: #fff;
  color: var(--dark-color, #323232);
  font-size: 0.85rem;
  font-weight: 650;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--grey-light-color, #f3f5f7);
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
}

.sig-btn--primary {
  background: var(--primary-color, #0076e5);
  border-color: var(--primary-color, #0076e5);
  color: #fff;

  &:hover:not(:disabled) {
    background: #0069cc;
  }
}
</style>
